import React from 'react';
import { PurchaseOrderApprovalRecord } from '@/types/domain/po-management';
import { formatCurrency, formatDate } from '@/lib/utils';
import { CheckCircle2, Clock, XCircle, ShieldCheck } from 'lucide-react';

interface PoApprovalsTimelineProps {
  approvals: PurchaseOrderApprovalRecord[];
  poCurrency: string;
}

export const PoApprovalsTimeline: React.FC<PoApprovalsTimelineProps> = ({
  approvals,
  poCurrency,
}) => {
  if (!approvals || approvals.length === 0) {
    return (
      <div className="p-8 text-center bg-card rounded-2xl border border-border">
        <ShieldCheck className="h-10 w-10 text-foreground-muted mx-auto mb-2" />
        <p className="text-xs font-semibold text-foreground-secondary">
          No approval workflow records associated with this Purchase Order.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 font-heading">
      <div className="flex items-center justify-between pb-2 border-b border-border/60">
        <div>
          <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> Multi-Tier Approval Chain & Delegation Governance
          </h3>
          <p className="text-[11px] text-foreground-secondary">
            Role-based financial delegation limits and authorization audit trail.
          </p>
        </div>
      </div>

      <div className="relative pl-6 space-y-6 before:absolute before:left-2.5 before:top-3 before:bottom-3 before:w-0.5 before:bg-border">
        {approvals.map((stage, idx) => {
          const isApproved = stage.status === 'APPROVED';
          const isRejected = stage.status === 'REJECTED';

          return (
            <div key={stage.id || idx} className="relative group">
              {/* Dot Icon */}
              <div
                className={`absolute -left-6 top-0.5 h-5 w-5 rounded-full flex items-center justify-center border-2 bg-card ${
                  isApproved
                    ? 'border-emerald-500 text-emerald-600'
                    : isRejected
                    ? 'border-rose-500 text-rose-600'
                    : 'border-amber-500 text-amber-500'
                }`}
              >
                {isApproved ? (
                  <CheckCircle2 className="h-3 w-3 fill-emerald-500 text-white" />
                ) : isRejected ? (
                  <XCircle className="h-3 w-3 fill-rose-500 text-white" />
                ) : (
                  <Clock className="h-3 w-3" />
                )}
              </div>

              {/* Stage Card */}
              <div className="p-4 rounded-xl border border-border bg-card/80 shadow-2xs space-y-2.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-bold tracking-wider uppercase text-primary">
                      Stage {stage.stageNumber} — {stage.stageName}
                    </span>
                    <h4 className="text-xs font-extrabold text-foreground">
                      {stage.approverName}{' '}
                      <span className="text-[11px] font-medium text-foreground-secondary">
                        ({stage.approverRole})
                      </span>
                    </h4>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold text-foreground-secondary">
                      Authority Limit: {formatCurrency(stage.approvalLimit, poCurrency)}
                    </span>
                    <span
                      className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${
                        isApproved
                          ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                          : isRejected
                          ? 'bg-rose-500/10 text-rose-700 border-rose-500/20'
                          : 'bg-amber-500/10 text-amber-700 border-amber-500/20'
                      }`}
                    >
                      {stage.status}
                    </span>
                  </div>
                </div>

                {stage.comments && (
                  <div className="p-2.5 rounded-lg bg-surface/70 border border-border/60 text-xs text-foreground font-medium">
                    <span className="text-[10px] font-bold text-foreground-secondary block uppercase mb-0.5">
                      Decision Note:
                    </span>
                    {stage.comments}
                  </div>
                )}

                {stage.actionDate && (
                  <div className="text-[10px] font-medium text-foreground-secondary">
                    Action recorded on {formatDate(stage.actionDate)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
